import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { reiseApi, type SammendragVisning } from '../../api/reise';
import SammendragModuler from './SammendragModuler';

interface Props {
  /** Åpner et spørsmål med svaret, så kunden kan endre det. */
  onEndreSvar: (sporsmalId: string, instans: string) => void;
  /** Tilbake til spørsmålene. */
  onFortsett: () => void;
  /** Feil som betyr at økten er borte. */
  onFeil: (e: unknown) => void;
}

/**
 * Sammendraget av reisen per modul, med kundens egne ord, flagg og motsigelser. Eieren bekrefter det, og det
 * låses som en versjon. Etterpå kan eieren åpne reisen for endringer, og neste bekreftelse gir en ny versjon.
 */
export default function SammendragSide({ onEndreSvar, onFortsett, onFeil }: Props) {
  const { t, i18n } = useTranslation();
  const overskrift = useRef<HTMLHeadingElement>(null);
  const [visning, setVisning] = useState<SammendragVisning | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const [opptatt, setOpptatt] = useState(false);

  const hent = useCallback(() => reiseApi.sammendrag().then(setVisning).catch(onFeil), [onFeil]);

  useEffect(() => {
    hent();
    overskrift.current?.focus();
  }, [hent]);

  async function utfor(handling: () => Promise<unknown>) {
    setOpptatt(true);
    setFeil(null);
    try {
      await handling();
      await hent();
    } catch (e) {
      if (e instanceof ApiFeil && e.status !== 401) {
        setFeil(e.kode);
        await hent();
      } else onFeil(e);
    } finally {
      setOpptatt(false);
    }
  }

  const dato = (iso: string | null) => (iso ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString(i18n.resolvedLanguage) : '');
  const laast = visning?.laast;
  const bekreftet = visning?.tilstand === 'bekreftet';
  const dokument = visning?.dokument;
  const sporsmalTekst = (id: string) => dokument?.moduler.flatMap((m) => m.svar).find((s) => s.sporsmalId === id)?.sporsmal ?? id;

  return (
    <section aria-labelledby="sammendrag-tittel">
      <h2 id="sammendrag-tittel" ref={overskrift} tabIndex={-1} className="mb-2 text-[1.375rem] font-semibold">
        {t('reise.sammendrag.tittel')}
      </h2>
      <p className="mb-4 text-slate-700">{t('reise.sammendrag.ingress')}</p>

      {!visning && <p role="status">{t('reise.sammendrag.laster')}</p>}

      {visning && dokument && (
        <>
          {laast && (
            <div className="mb-4 melding-ok">
              <p className="flex items-center gap-2">
                <Lock aria-hidden="true" className="h-4 w-4 shrink-0" />
                {t('reise.sammendrag.laast', { versjon: laast.versjon, dato: dato(laast.bekreftet), av: laast.bekreftetAv })}
              </p>
              {visning.endretSidenLaast && !bekreftet && (
                <p className="mt-2">{t('reise.sammendrag.endret', { versjon: laast.versjon, neste: laast.versjon + 1 })}</p>
              )}
            </div>
          )}

          {bekreftet && !visning.hindringer.includes('bare_eier') && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
              <p className="mb-3 text-slate-700">{t('reise.sammendrag.aapneTekst')}</p>
              <button type="button" disabled={opptatt} onClick={() => utfor(() => reiseApi.endreEtterBekreftelse())} className="knapp-sekundaer">
                {t('reise.sammendrag.aapne')}
              </button>
            </div>
          )}

          {dokument.ubesvarte > 0 && (
            <div className="mb-4 flex flex-col gap-3 melding-advarsel sm:flex-row sm:items-center sm:justify-between">
              <p>{t('reise.sammendrag.ubesvarte', { antall: dokument.ubesvarte })}</p>
              <button type="button" onClick={onFortsett} className="knapp-sekundaer">
                {t('reise.sammendrag.fortsett')}
              </button>
            </div>
          )}

          {dokument.motsigelser.length > 0 && (
            <div className="mb-4 melding-feil">
              <p className="mb-2 flex items-center gap-2 font-medium">
                <AlertTriangle aria-hidden="true" className="h-5 w-5 shrink-0" />
                {t('reise.sammendrag.motsigelser')}
              </p>
              <ul className="flex flex-col gap-3">
                {dokument.motsigelser.map((m) => (
                  <li key={`${m.modulId}.${m.id}`}>
                    <p className="mb-2">{m.tekst}</p>
                    {!bekreftet && (
                      <div className="flex flex-wrap gap-2">
                        {m.sporsmalIder.map((id) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => onEndreSvar(id, '')}
                            aria-label={t('reise.sammendrag.endreSvar', { sporsmal: sporsmalTekst(id) })}
                            className="knapp-sekundaer text-sm"
                          >
                            {t('reise.sammendrag.endre')}: {sporsmalTekst(id)}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <SammendragModuler dokument={dokument} onEndreSvar={bekreftet ? undefined : onEndreSvar} />

          {feil && (
            <p role="alert" className="mb-4 melding-feil">
              {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
            </p>
          )}

          {!bekreftet && visning.hindringer.includes('bare_eier') && <p className="text-slate-700">{t('reise.sammendrag.bareEier')}</p>}

          {visning.kanBekreftes && (
            <div className="kort p-5">
              <p className="mb-3 text-slate-700">{t('reise.sammendrag.bekreftTekst', { versjon: (laast?.versjon ?? 0) + (visning.endretSidenLaast || !laast ? 1 : 0) })}</p>
              <button
                type="button"
                disabled={opptatt}
                onClick={() => utfor(() => reiseApi.bekreftSammendrag(visning.fingeravtrykk))}
                className="knapp-primaer inline-flex items-center gap-2"
              >
                <CheckCircle2 aria-hidden="true" className="h-5 w-5" />
                {t('reise.sammendrag.bekreft')}
              </button>
            </div>
          )}

          <p className="mt-6 text-xs text-slate-500">
            {t('reise.sammendrag.innholdsversjon', { versjon: dokument.innholdVersjon, fingeravtrykk: visning.fingeravtrykk.slice(0, 12) })}
          </p>
        </>
      )}
    </section>
  );
}
